/**
 * How the editor's region overlay (EditorViewerInstance.setRegionEditorOverlay) draws one area, so
 * a creator can tell whose shape is whose: every area in its region's colour; while a region is
 * open in the Regions panel, the other regions' areas dimmed; the selected area brighter, thicker
 * and with its points marked.
 */
export type Rgb = readonly [number, number, number];

export interface RegionOverlayStyle {
    /** Line colour, rgb 0..1. */
    color: [number, number, number];
    /** Draw it thicker (the selected area). */
    thick: boolean;
    /** Mark its points (the selected area's outline). */
    points: boolean;
}

/** How much an area of a region that isn't open keeps of its colour. */
export const OVERLAY_DIM = 0.6;
/** How far the selected area's colour moves toward white. */
export const OVERLAY_SELECTED_LIFT = 0.35;

export function regionOverlayStyle(
    regionColor: Rgb,
    options: { regionId: string; areaId: string; selectedRegionId?: string | null; selectedAreaId?: string | null },
): RegionOverlayStyle {
    const [r, g, b] = regionColor;
    if (options.selectedAreaId && options.areaId === options.selectedAreaId) {
        const lift = (v: number) => v + (1 - v) * OVERLAY_SELECTED_LIFT;
        return { color: [lift(r), lift(g), lift(b)], thick: true, points: true };
    }
    const otherRegionOpen = !!options.selectedRegionId && options.selectedRegionId !== options.regionId;
    const k = otherRegionOpen ? OVERLAY_DIM : 1;
    return { color: [r * k, g * k, b * k], thick: false, points: false };
}

/**
 * World-space offsets that draw a line about three pixels wide with `drawLine` (one pixel): the
 * line again, nudged along the camera's right and up by a fraction of its distance.
 */
export function thickLineOffsets(
    right: { x: number; y: number; z: number },
    up: { x: number; y: number; z: number },
    distance: number,
): Array<[number, number, number]> {
    const d = Math.max(1e-4, distance) * 0.0012;
    return [
        [right.x * d, right.y * d, right.z * d],
        [-right.x * d, -right.y * d, -right.z * d],
        [up.x * d, up.y * d, up.z * d],
        [-up.x * d, -up.y * d, -up.z * d],
    ];
}
